import React, { useEffect, useState } from 'react';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Button, Card, Input, Radio, Row, Spin } from 'antd';
import { observer } from 'mobx-react';
import UserStore from '../stores/UserStore';
import UIStore from '../stores/UIStore';

import { ErrorRender } from '@partsbadger/library';
import { FormComponentProps } from '@ant-design/compatible/es/form';
import { isFreeEmailDomain } from '../utils/utils';

const RadioButton = Radio.Button;
const RadioGroup = Radio.Group;

interface Props extends FormComponentProps {
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    company?: string | null;
    handleLogin?: (payload?: { quoteData?: any; userData?: any }) => void;
    bordered?: boolean;
    onClose?: () => void;
}

interface IHandleSubmit {
    preventDefault: () => void;
}

const Auth = observer((props: Props) => {
    const [isNew, setIsNew] = useState<boolean>(true);
    const [email, setEmail] = useState<string | null>(null);
    const [resettingPassword, setResettingPassword] = useState<boolean>(false);
    const [passwordResetSent, setPasswordResetSent] = useState<boolean>(false);
    const [loading, setLoading] = useState(false);
    const [emailDomains, setEmailDomains] = useState<string[]>([]);
    const { bordered = true, onClose } = props;
    useEffect(() => {
        UIStore.setSelectedKey(['my-account']);
        UIStore.setDocumentTitle('Login');
        UserStore.getEmailDomains()
            .then(res => {
                const domainList = res.results?.map((item: { id: number; domain: string }) =>
                    item?.domain?.toLowerCase()
                );
                setEmailDomains(domainList);
            })
            .catch(err => {
                console.error('Error fetching email domains:', err);
            });
    }, []);

    const submitHandler = (e: IHandleSubmit) => {
        e.preventDefault();
        props.form.validateFieldsAndScroll((err, values) => {
            if (!err) {
                setLoading(true);

                if (isNew) {
                    const data = {
                        first_name: values.first_name,
                        last_name: values.last_name,
                        company: values.company,
                        email: values.email?.toLowerCase(),
                        password1: values.password1,
                        password2: values.password2,
                    };

                    UserStore.register(data)
                        .then((values: any) => {
                            if (props?.handleLogin) {
                                if (values?.quoteData) {
                                    props.handleLogin({
                                        quoteData: values?.quoteData,
                                        userData: values?.userData,
                                    });
                                }
                                props.handleLogin();
                            }
                            setLoading(false);
                        })
                        .catch(err => {
                            console.error('Error registering:', err);
                            setLoading(false);
                        });
                } else {
                    UserStore.login(values.email, values.password1)
                        .then((values: any) => {
                            if (props?.handleLogin) {
                                if (values?.quoteData) {
                                    props.handleLogin({
                                        quoteData: values?.quoteData,
                                        userData: values?.userData,
                                    });
                                }
                                props.handleLogin();
                                setLoading(false);
                            }
                        })
                        .catch(err => {
                            console.error('Error logging in:', err);
                            setLoading(false);
                        });
                }
            }
        });
    };

    const sendPasswordReset = (e: IHandleSubmit) => {
        e.preventDefault();
        if (email) {
            UserStore.resetPassword(email);
            setPasswordResetSent(true);
        }
    };

    const resetPasswordSent = (e: IHandleSubmit) => {
        e.preventDefault();
        setResettingPassword(false);
        setPasswordResetSent(false);
    };

    const compareToFirstPassword = (rule: any, value: any, callback: any) => {
        const { form } = props;
        if (value && value !== form.getFieldValue('password1')) {
            callback('Two passwords that you enter is inconsistent!');
        } else {
            callback();
        }
    };

    useEffect(() => {
        UIStore.setDocumentTitle('Login');
    }, []);

    let errorMessage: React.ReactNode | null = null;

    if (UserStore.errors) {
        errorMessage = <ErrorRender error={UserStore.errors} />;
    }

    const { getFieldDecorator, getFieldValue } = props.form;

    return (
        <Card style={{ minWidth: 320, width: 550, maxWidth: '100%' }} bordered={bordered}>
            <Spin spinning={loading}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    {isNew ? (
                        <div className={'font-bold text-xl'}>Register</div>
                    ) : (
                        <div className={'font-bold text-xl'}>Login</div>
                    )}
                    <RadioGroup
                        onChange={() => {
                            setResettingPassword(false);
                            setIsNew(!isNew);
                        }}
                        defaultValue="2"
                        size="small"
                    >
                        <RadioButton value="1">Login</RadioButton>
                        <RadioButton value="2" onClick={() => {}}>
                            Register
                        </RadioButton>
                    </RadioGroup>
                </div>
                <div className={'mt-2'}>{errorMessage}</div>
                {!resettingPassword ? (
                    <Form onSubmit={submitHandler} layout={'vertical'}>
                        {isNew && (
                            <React.Fragment>
                                <Form.Item label={'First Name'} hasFeedback>
                                    {getFieldDecorator('first_name', {
                                        rules: [{ required: true, message: 'Please insert a value' }],
                                        initialValue: props?.first_name || '',
                                    })(<Input autoComplete={'off'} />)}
                                </Form.Item>

                                <Form.Item label={'Last Name'} hasFeedback>
                                    {getFieldDecorator('last_name', {
                                        rules: [{ required: true, message: 'Please insert a value' }],
                                        initialValue: props?.last_name || '',
                                    })(<Input autoComplete={'off'} />)}
                                </Form.Item>

                                <Form.Item
                                    label={
                                        isFreeEmailDomain(getFieldValue('email'), emailDomains)
                                            ? 'Company (Optional)'
                                            : 'Company'
                                    }
                                    hasFeedback
                                >
                                    {getFieldDecorator('company', {
                                        rules: [
                                            {
                                                required: !isFreeEmailDomain(getFieldValue('email'), emailDomains),
                                                message: 'Please insert a value',
                                            },
                                        ],
                                        initialValue: props?.company || '',
                                    })(<Input autoComplete={'off'} />)}
                                </Form.Item>
                            </React.Fragment>
                        )}
                        <Form.Item label={'Email'} hasFeedback>
                            {getFieldDecorator('email', {
                                validateTrigger: 'onBlur',
                                rules: [
                                    {
                                        type: 'email',
                                        required: true,
                                        message: 'Please insert a valid email',
                                    },
                                ],
                                initialValue: props?.email || UserStore.guest_email,
                            })(<Input onChange={event => setEmail(event.target.value)} autoComplete={'username'} />)}
                        </Form.Item>

                        <Form.Item label={'Password'} hasFeedback>
                            {getFieldDecorator('password1', {
                                rules: [
                                    {
                                        required: true,
                                        message: 'Password is required',
                                    },
                                    {
                                        min: isNew ? 8 : 1,
                                        message: 'Password must have at least 8 characters',
                                    },
                                ],
                            })(<Input.Password type={'password'} autoComplete={'current-password'} />)}
                        </Form.Item>

                        {isNew && (
                            <Form.Item label={'Confirm Password'} hasFeedback>
                                {getFieldDecorator('password2', {
                                    rules: [
                                        {
                                            required: true,

                                            message: 'Password Confirmation is required',
                                        },
                                        {
                                            validator: compareToFirstPassword,
                                        },
                                    ],
                                })(<Input.Password type={'password'} autoComplete={'new-password'} />)}
                            </Form.Item>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <div className={'flex flex-row gap-2'}>
                                <Button type="primary" loading={loading} htmlType="submit">
                                    {isNew ? 'Register' : 'Login'}
                                </Button>
                                {onClose && (
                                    <Button type="default" loading={loading} onClick={onClose}>
                                        Cancel
                                    </Button>
                                )}
                            </div>
                            {!isNew && (
                                <span
                                    className="cursor-pointer text-red-700 mt-4"
                                    onClick={() => setResettingPassword(true)}
                                >
                                    Forgot your password
                                </span>
                            )}
                        </div>
                    </Form>
                ) : !passwordResetSent ? (
                    <Form onSubmit={sendPasswordReset} layout={'vertical'}>
                        <p>To reset your password, please input your email address.</p>
                        <Form.Item style={{ marginBottom: '20px' }} label={'Email'} hasFeedback>
                            {getFieldDecorator('email', {
                                rules: [
                                    {
                                        type: 'email',
                                        required: true,
                                        message: 'Please insert a valid email',
                                    },
                                ],
                            })(<Input onChange={event => setEmail(event.target.value)} autoComplete={'email'} />)}
                        </Form.Item>

                        <Row justify="space-between">
                            <Button disabled={!email} type="primary" htmlType="submit">
                                Send Password Reset Instructions
                            </Button>
                            <p style={{ color: '#c00', cursor: 'pointer' }} onClick={() => setResettingPassword(false)}>
                                Cancel
                            </p>
                        </Row>
                    </Form>
                ) : (
                    <Form onSubmit={resetPasswordSent} layout={'vertical'}>
                        <h3 style={{ marginBottom: '20px', color: 'red' }}>
                            An email has been sent to {email} to reset your password.
                        </h3>
                        <Button type="primary" htmlType="submit">
                            Ok
                        </Button>
                    </Form>
                )}
            </Spin>
        </Card>
    );
});

export const LoginForm = Form.create<Props>()(Auth);
